import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import { useOnChange } from "@prismicio/editor-support/React"
import { cache, useRequest } from "@prismicio/editor-support/Suspense"
import type { ErrorBoundary } from "@prismicio/editor-ui"
import {
  FilledDocumentLinkContent,
  FilledExternalLinkContent,
  FilledFileLinkContent,
  FilledImageLinkContent,
  type LinkContent
} from "@prismicio/types-internal/lib/content"
import type { Link } from "@prismicio/types-internal/lib/customtypes"
import { useRef, useState } from "react"
import { object, string, type TypeOf } from "zod"

import { defaultImageDimension } from "../../constants/images"
import type { MediaAssetOrExternalImage } from "../../core/MediaLibrary/hooks/mediaLibraryData"
import {
  type AuthStrategy,
  throwConfigError,
  useEditorConfig
} from "../../EditorConfig"
import { getFieldPlaceholder } from "../utils"
import type { FieldSelectType } from "./LinkFieldActions"

interface useLinkFieldParams {
  field: Link
  content: LinkContent | undefined
  onContentChange: (content: LinkContent | undefined) => void
}

export function useLinkField(params: useLinkFieldParams) {
  const { field, content, onContentChange } = params

  const fieldValue = getFieldValue()

  const displayTextEnabled = field.config?.allowText ?? false
  const displayText = (displayTextEnabled && content?.value.text) || undefined

  const [contentType, setContentType] = useState(getContentType)

  const linkTarget =
    (FilledExternalLinkContent.is(content?.value) && content?.value.target) ||
    undefined

  const [hasError, setHasError] = useState(false)
  const errorBoundaryRef = useRef<ErrorBoundary>(null)

  useOnChange(fieldValue, onFieldValueChange)

  const selectType = contentType
    ? contentTypeToSelectMap[contentType]
    : undefined

  function getContentType() {
    if (!field.config?.select || content?.value.__TYPE__)
      return content?.value.__TYPE__

    return selectToContentTypeMap[field.config?.select]
  }

  function getFieldValue() {
    if (!content) return ""

    if (FilledDocumentLinkContent.is(content.value)) return content.value.id
    if (FilledExternalLinkContent.is(content.value)) return content.value.url
    if (
      FilledFileLinkContent.is(content.value) ||
      FilledImageLinkContent.is(content.value)
    )
      return content.value.name

    return ""
  }

  function updateContentWithNoLink(displayText?: string) {
    if (!displayText) return onContentChange(undefined)

    switch (field.config?.select) {
      case "web":
        return onContentChange({
          __TYPE__: "LinkContent",
          value: {
            __TYPE__: "ExternalLink",
            kind: "web",
            text: displayText
          }
        })
      case "document":
        return onContentChange({
          __TYPE__: "LinkContent",
          value: {
            __TYPE__: "DocumentLink",
            kind: "document",
            text: displayText
          }
        })
      case "media":
        return onContentChange({
          __TYPE__: "LinkContent",
          value: { __TYPE__: "MediaLink", kind: "media", text: displayText }
        })
      default:
        return onContentChange({
          __TYPE__: "LinkContent",
          value: { __TYPE__: "AnyLink", text: displayText }
        })
    }
  }

  function updateLinkContent(value: string, newTab?: boolean) {
    if (!value) return updateContentWithNoLink(displayText)

    const newTarget = newTab ? "_blank" : undefined

    switch (contentType) {
      case "ExternalLink":
        return onContentChange({
          __TYPE__: "LinkContent",
          value: {
            __TYPE__: "ExternalLink",
            url: value,
            target: newTarget,
            text: displayText
          }
        })
      case "DocumentLink":
        return onContentChange({
          __TYPE__: "LinkContent",
          value: {
            __TYPE__: "DocumentLink",
            id: value,
            text: displayText
          }
        })
      default:
        return onContentChange(undefined)
    }
  }

  function updateDisplayTextContent(displayText: string) {
    if (content) {
      return onContentChange({
        __TYPE__: content.__TYPE__,
        value: { ...content.value, text: displayText }
      })
    }

    return updateContentWithNoLink(displayText)
  }

  function onTypeChange(newValue?: FieldSelectType) {
    setContentType(newValue ? selectToContentTypeMap[newValue] : undefined)
    updateLinkContent("")
  }

  function onDocumentSelected(documentId: string) {
    updateLinkContent(documentId)
  }

  function onError() {
    setHasError(true)
  }

  function onFieldValueChange() {
    if (!hasError) return
    setHasError(false)
    errorBoundaryRef.current?.reset()
  }

  function onMediaSelected(newMedia: MediaAssetOrExternalImage) {
    const mappedMedia = mapMediaToLinkContent({ media: newMedia })
    switch (mappedMedia.value.__TYPE__) {
      case "ImageLink":
        return onContentChange({
          __TYPE__: mappedMedia.__TYPE__,
          value: {
            ...mappedMedia.value,
            text: displayText
          }
        })
      case "FileLink":
        return onContentChange({
          __TYPE__: mappedMedia.__TYPE__,
          value: {
            ...mappedMedia.value,
            text: displayText
          }
        })
    }
  }

  return {
    contentType,
    selectType,
    fieldValue,
    onDisplayTextChange: updateDisplayTextContent,
    onValueChange: updateLinkContent,
    onTypeChange,
    placeholder: getFieldPlaceholder(field),
    isTypeSelectEnabled: !field.config?.select,
    onDocumentSelected,
    onError,
    errorBoundaryRef,
    isTargetNewTab: linkTarget === "_blank",
    onMediaSelected,
    displayTextEnabled,
    displayText: displayText ?? ""
  }
}

const contentTypeToSelectMap = {
  AnyLink: "any",
  DocumentLink: "document",
  ExternalLink: "web",
  FileLink: "media",
  ImageLink: "media",
  MediaLink: "media"
} as const

const selectToContentTypeMap = {
  any: "AnyLink",
  document: "DocumentLink",
  web: "ExternalLink",
  media: "FileLink"
} as const

export function useDocumentTitle(id: string) {
  const config = useEditorConfig()

  if (!config.searchDocuments) throwConfigError("searchDocuments")

  const { coreApiBaseUrl, authStrategy } = config

  const response = useRequest(getDocumentTitle, [
    coreApiBaseUrl,
    authStrategy,
    id
  ])

  return response.title
}

const documentMeta = object({
  id: string(),
  title: string()
})

type DocumentMeta = TypeOf<typeof documentMeta>

function getDocumentTitle(
  baseUrl: URL,
  authStrategy: AuthStrategy,
  id: string
): Promise<DocumentMeta> {
  return request(new URL(`documents/${id}`, baseUrl), {
    credentials: getRequestCredentials(authStrategy),
    okType: documentMeta
  })
}

// Translating a document ID to its title is nice but quite low priority.
// However, some documents have hundreds of content relationships in a single Group
// so if they scroll fast, we can't have them wait for minutes to see the data.
// There's already a limiting factor when it comes to the number of LinkField requests we do in parallel:
// The GroupItems are lazily rendered.
cache.registerFetcherConfig(getDocumentTitle, { concurrency: 50 })

interface MapMediaToLinkContentArgs {
  media: MediaAssetOrExternalImage
}

export function mapMediaToLinkContent(args: MapMediaToLinkContentArgs) {
  const { media } = args
  const isMediaLibraryItem = "kind" in media
  const isImage = isMediaLibraryItem && media.kind === "image"

  if (isImage) {
    return {
      __TYPE__: "LinkContent",
      value: {
        __TYPE__: "ImageLink",
        id: media.id,
        name: media.filename,
        url: media.url,
        kind: media.kind,
        size: media.size.toString(),
        height: media.height?.toString() ?? `${defaultImageDimension}`,
        width: media.width?.toString() ?? `${defaultImageDimension}`,
        date: media.last_modified?.toString() ?? ""
      }
    } satisfies LinkContent
  } else if (isMediaLibraryItem) {
    return {
      __TYPE__: "LinkContent",
      value: {
        __TYPE__: "FileLink",
        id: media.id,
        name: media.filename,
        kind: media.kind,
        url: media.url,
        size: media.size.toString(),
        date: media.last_modified?.toString() ?? ""
      }
    } satisfies LinkContent
  } else {
    // External image
    return {
      __TYPE__: "LinkContent",
      value: {
        __TYPE__: "ImageLink",
        id: media.id,
        name: media?.alt ?? media.url,
        kind: "image",
        url: media.url,
        size: "0",
        height: media.height?.toString() ?? `${defaultImageDimension}`,
        width: media.width?.toString() ?? `${defaultImageDimension}`
      }
    } satisfies LinkContent
  }
}
